import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  ExitIcon,
  GridIcon,
  HomeIcon,
  MagnifyingGlassIcon,
  MoonIcon,
  SunIcon,
} from '@radix-ui/react-icons'
import { Button } from '@/components/ui'
import { getInternalToolKeyForRoute } from '@/lib/internalToolAccess'
import { internalTools } from '@/lib/internalToolRegistry'
import { cn } from '@/lib/utils'
import colorMark from '@/assets/brand/residesk-logo-mark-color.svg'
import whiteMark from '@/assets/brand/residesk-logo-mark-white.svg'

const getUserDisplayName = (user) =>
  [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'Signed in user'

const getUserInitials = (user) => {
  const nameInitials = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .map((part) => part.trim().charAt(0))
    .join('')

  return (nameInitials || user?.email?.trim().charAt(0) || 'U').toUpperCase()
}

function UserAvatar({ user }) {
  const [imageFailed, setImageFailed] = useState(false)
  const showImage = Boolean(user?.avatarUrl) && !imageFailed
  const piiType = user?.firstName || user?.lastName ? 'name' : 'email'

  return (
    <div
      className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-primary text-xs font-semibold text-primary-foreground shadow-sm"
      role="img"
      aria-label={`Signed in as ${getUserDisplayName(user)}`}
      data-pii={piiType}
    >
      {showImage ? (
        <img
          src={user.avatarUrl}
          alt=""
          className="h-full w-full object-cover"
          referrerPolicy="no-referrer"
          onError={() => setImageFailed(true)}
        />
      ) : (
        getUserInitials(user)
      )}
    </div>
  )
}

export function AppHeader({ user, onLogout, dark, onToggleDark, onOpenLauncher }) {
  const location = useLocation()
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef(null)
  const activeToolKey = getInternalToolKeyForRoute(location.pathname, internalTools)
  const activeTool = internalTools.find((item) => item.key === activeToolKey)
  const isHome = location.pathname === '/'

  useEffect(() => {
    setUserMenuOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!userMenuOpen) return undefined

    const closeOnOutsidePress = (event) => {
      if (!userMenuRef.current?.contains(event.target)) setUserMenuOpen(false)
    }
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setUserMenuOpen(false)
    }

    document.addEventListener('pointerdown', closeOnOutsidePress)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePress)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [userMenuOpen])

  return (
    <header className="relative z-40 flex h-16 shrink-0 items-center gap-2 border-b border-border bg-card px-3 sm:px-5">
      <Button
        variant="ghost"
        asChild
        className="h-[44px] shrink-0 gap-2 px-2 sm:px-3"
        data-cy="home-link"
      >
        <Link to="/" aria-label="Open Launch Center">
          <img src={dark ? whiteMark : colorMark} alt="" className="h-6 w-auto" />
          <span
            className={cn(
              'font-semibold text-foreground',
              isHome ? 'hidden sm:inline' : 'inline',
            )}
          >
            {isHome ? 'ResiDesk' : 'Home'}
          </span>
          {!isHome && <HomeIcon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />}
        </Link>
      </Button>

      <Button
        type="button"
        variant="ghost"
        className="h-[44px] shrink-0 gap-2 px-2 sm:px-3"
        onClick={onOpenLauncher}
        aria-label="Open app launcher"
        data-cy="app-launcher-trigger"
      >
        <GridIcon className="h-5 w-5" aria-hidden="true" />
        <span>Apps</span>
      </Button>

      {activeTool && (
        <div className="hidden min-w-0 items-center gap-2 border-l border-border pl-4 md:flex">
          <span className="truncate text-sm font-medium text-foreground">{activeTool.title}</span>
        </div>
      )}

      <div className="ml-auto flex items-center gap-1 sm:gap-2">
        <Button
          type="button"
          variant="outline"
          className="h-[44px] w-[44px] shrink-0 gap-2 px-0 sm:w-auto sm:min-w-56 sm:justify-between sm:px-3"
          onClick={onOpenLauncher}
          aria-label="Search apps"
          data-cy="global-tool-search"
        >
          <span className="flex items-center gap-2">
            <MagnifyingGlassIcon className="h-4 w-4" aria-hidden="true" />
            <span className="hidden text-muted-foreground sm:inline">Search apps</span>
          </span>
          <kbd
            className="hidden rounded-sm border border-border bg-muted px-1.5 py-0.5 text-xs text-muted-foreground sm:inline"
            aria-hidden="true"
          >
            ⌘K
          </kbd>
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-[44px] w-[44px] shrink-0"
          onClick={onToggleDark}
          aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
          title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {dark ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
        </Button>

        <div className="relative" ref={userMenuRef}>
          <button
            type="button"
            onClick={() => setUserMenuOpen((open) => !open)}
            aria-expanded={userMenuOpen}
            aria-haspopup="menu"
            aria-label="Open user menu"
            className="flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <UserAvatar key={user?.avatarUrl || user?.email} user={user} />
          </button>
          {userMenuOpen && (
            <div
              role="menu"
              className="absolute right-0 top-12 w-72 rounded-lg border border-border bg-popover p-2 text-popover-foreground shadow-lg"
            >
              <div className="border-b border-border px-3 py-2">
                <p
                  className="truncate text-sm font-medium"
                  data-pii={user?.firstName || user?.lastName ? 'name' : 'email'}
                >
                  {getUserDisplayName(user)}
                </p>
                <p className="truncate text-xs text-muted-foreground" data-pii="email">
                  {user.email}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                className="mt-1 w-full justify-start gap-3"
                onClick={() => {
                  onToggleDark()
                  setUserMenuOpen(false)
                }}
                role="menuitem"
              >
                {dark ? <SunIcon className="h-4 w-4" /> : <MoonIcon className="h-4 w-4" />}
                {dark ? 'Light mode' : 'Dark mode'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className={cn('w-full justify-start gap-3 text-muted-foreground hover:text-foreground')}
                onClick={onLogout}
                role="menuitem"
              >
                <ExitIcon className="h-4 w-4" />
                Sign out
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
